import { Button, Dropdown, Notification, toast } from "components/ui";
import { DROPDOWN_ITEM_TYPE, PLACEMENT } from "components/ui/utils/constant";
import { useEffect, useState } from "react";
import { BsStars } from "react-icons/bs";
import { TbPencilStar, TbTablePlus } from "react-icons/tb";
import { getFileTypeImageVideo } from "utils/fileType";
import useEditorCallback from "utils/hooks/useEditorCallback";
import { BiSolidMagicWand } from "react-icons/bi";
import { usePinterest } from "utils/hooks/socials/usePinterest";
import { FcStackOfPhotos } from "react-icons/fc";
import { Trans } from "react-i18next";
import { HiCheckCircle, HiOutlineCheck, HiOutlinePlusCircle } from "react-icons/hi2";
import { MdOutlineDashboard } from "react-icons/md";
import InputModern from "components/ui/Input/InputModern";

const BoardDropdown = ({
    post,
    isEditable = true,
    onChange = (board_id) => void 0,
    portal = false
}) => {

    const [ isLoading, setIsLoading ] = useState(false);
    const [ boards, setBoards ] = useState([]);
    const [ newBoardName, setNewBoardName ] = useState("");
    const [ showCreateBoard, setShowCreateBoard ] = useState(false);
    const [ isCreatingBoard, setIsCreatingBoard ] = useState(false);
    const { getPinterestBoards, createPinterestBoard } = usePinterest();

    const board = boards?.find(e => e.id == post?.preferences?.board_id);

    const handleOnCreateBoard = () => {
        setShowCreateBoard(true);
    }

    const resetState = () => {
        setNewBoardName("");
        setShowCreateBoard(false);
    }

    const createBoard = async () => {
        setIsCreatingBoard(true);
        const newBoard = await createPinterestBoard(newBoardName)
            .then((board) => {
                if (board?.created_at) {
                    return board;
                } else {
                    toast.push(<Notification title={<Trans i18nKey={`companyProfile.companyUpdateError`}>Error creating board. Please try again. {board?.message || "Unknown error."}</Trans>} type="danger" />, {
                        placement: 'top-center',
                    })
                }
            })
            .catch((error) => {
                toast.push(<Notification title={<Trans i18nKey={`companyProfile.companyUpdateError`}>Error creating board. Please try again. {error?.message || error || "Unknown error."}</Trans>} type="danger" />, {
                    placement: 'top-center',
                })
            });
        if (newBoard?.id) {
            await new Promise(r => setTimeout(r, 2000));
            await getBoards(newBoard);
        }
        setShowCreateBoard(false);
        setIsCreatingBoard(false);
    }
    
    const getBoards = async (board = undefined) => {
        setIsLoading(true);
        const _boards = await getPinterestBoards().then((_boards) => _boards); 
        setBoards(_boards);
        if (board) onChange(board?.id);
        setIsLoading(false);
    }

    useEffect(() => {
        getBoards()
    }, []);

    return (
        <Dropdown
            placement={PLACEMENT.BOTTOM_START}
            parentClassName="w-full"
            menuClass="w-auto max-w-[300px] max-h-[300px] overflow-y-auto"
            portal={portal}
            disabled={isLoading || !isEditable}
            renderTitle={
                <Button
                    shape="rounded"
                    size={"sm"}
                    className="!rounded-none w-full !p-4 !h-auto border-b border-gray-100 dark:border-gray-700"
                    onClick={resetState}
                    loading={isLoading}
                    disabled={isLoading || !isEditable}
                >
                    {isLoading 
                        ? <>Loading Boards...</>
                        : !!board
                            ? <div className='flex items-center gap-2'>
                                {board?.media?.image_cover_url 
                                    ? <img src={`${board?.media?.image_cover_url}`} className="h-6 w-6 object-cover shrink-0" />
                                    : <MdOutlineDashboard className='text-primary-600 text-2xl shrink-0' />
                                }
                                <span>{board?.name}</span>
                            </div>
                            :  <div className='flex items-center gap-2'>
                                <MdOutlineDashboard className='text-2xl shrink-0' />
                                <span><Trans i18nKey={`postModalPinterest.selectPinterestBoard`}>Select Pinterest Board</Trans></span>
                            </div>
                    }
                </Button>
            }
        >
            {!!showCreateBoard
                ? <div className="px-2 w-full flex items-center gap-2">
                    <InputModern 
                        autoFocus
                        placeholder="New board name..."
                        fieldsetClassName="w-full w-[300px] !bg-transparent flex-1"
                        className="rounded-2xl !bg-transparent w-full w-[300px]"
                        onChange={(e) => setNewBoardName(e.target.value)}
                        onKeyDown={(e) => { if (e.key == 'Enter') createBoard() }}
                        disabled={isCreatingBoard}
                        value={newBoardName}
                    />
                    <Button 
                        variant="twoTone"
                        size="sm"
                        className="rounded-full shrink-0"
                        loading={isCreatingBoard}
                        onClick={createBoard}
                        icon={<HiOutlineCheck />}
                    />
                </div>
                : <>
                    <Dropdown.Item className="!p-0">
                        <div onClick={(e) => { handleOnCreateBoard(); e.stopPropagation(); }} className="w-full h-ful flex items-center gap-2 cursor-pointer py-2 px-4">
                            <TbTablePlus className='h-6 md:text-2xl text-violet-600' />
                            <span>Create new board...</span>
                        </div>
                    </Dropdown.Item>
                    <Dropdown.Item variant="divider" />
                    {boards?.map((_board, key) => (
                        <Dropdown.Item key={key} onClick={() => onChange(_board?.id)} className={`flex items-center gap-2 py-2 px-4 ${board?.id == _board?.id && `bg-primary-50 dark:bg-primary-900`}`}>
                            {_board?.media?.image_cover_url 
                                ? <img src={`${_board?.media?.image_cover_url}`} className="w-6 h-6 object-cover rounded shrink-0" />
                                : <MdOutlineDashboard className='text-2xl text-primary-600 shrink-0' />
                            }
                            <span className="text-ellipsis block">{_board?.name}</span>
                        </Dropdown.Item>
                    ))}
                </>
            }
        </Dropdown>
    )
}

export default BoardDropdown;